import { Quote, Star, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import Reveal from "@/components/Reveal";
import Seo from "@/components/Seo";
import { TESTIMONIALS, IMAGES } from "@/lib/site";
import { FinalCTA } from "./Home";

export default function Testimonials() {
  return (
    <div data-testid="testimonials-page" className="bg-white">
      <Seo
        title="Client Testimonials | Mahaney Law"
        description="Read verified client reviews for Massachusetts trial attorney Joseph M. Mahaney across personal injury and other matters."
        path="/testimonials"
      />
      {/* Header — split hero with featured client quote */}
      <section data-testid="testimonials-hero" className="relative overflow-hidden bg-navy-950 md:flex md:items-center md:min-h-[calc(100vh-6rem)]">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-[0.5]"
          style={{ backgroundImage: `url(${IMAGES.hero_testimonials})` }}
          aria-hidden="true"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-navy-950/85 via-navy-950/65 to-navy-950/55" />
        <div className="relative w-full max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-20 md:py-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            <div className="lg:col-span-6">
              <Reveal>
                <div className="flex items-center gap-3">
                  <div className="flex gap-1">
                    {[...Array(5)].map((_, k) => (
                      <Star key={k} className="h-5 w-5 fill-gold text-gold" />
                    ))}
                  </div>
                  <span className="text-white/55 text-sm font-medium">
                    5.0 average client rating
                  </span>
                </div>
                <div className="eyebrow text-gold mt-6">Testimonials</div>
                <h1 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-[4.5rem] leading-[1.05] tracking-tight text-white mt-5 text-balance">
                  In their
                  <span className="block italic text-gold">own words.</span>
                </h1>
                <p className="mt-7 max-w-xl text-lg md:text-[1.15rem] text-white/70 leading-relaxed font-light">
                  Client trust is what we work hardest to earn. The reviews below
                  come from clients we've represented across personal injury,
                  motor vehicle accidents, and related matters.
                </p>
                <Link
                  to="/contact"
                  data-testid="testimonials-hero-cta"
                  className="inline-flex items-center justify-center gap-2 mt-9 px-8 py-4 rounded-sm bg-gold text-navy-950 text-sm font-semibold hover:bg-gold-dark transition-all"
                >
                  Become Our Next Client
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Reveal>
            </div>
            <div className="lg:col-span-6">
              <Reveal delay={0.12}>
                <div className="relative rounded-sm border border-white/10 bg-white/[0.04] backdrop-blur-sm p-8 md:p-10 shadow-[0_40px_90px_-50px_rgba(0,0,0,0.9)]">
                  <Quote className="h-10 w-10 text-gold" />
                  <p className="mt-6 font-display text-2xl md:text-[1.65rem] leading-snug italic text-white">
                    “{TESTIMONIALS[0].quote}”
                  </p>
                  <div className="mt-8 pt-6 border-t border-white/10 flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium text-white">
                        {TESTIMONIALS[0].name}
                      </div>
                      <div className="text-xs uppercase tracking-[0.18em] mt-1 text-white/45">
                        {TESTIMONIALS[0].matter}
                      </div>
                    </div>
                    <div className="flex gap-0.5">
                      {[...Array(5)].map((_, k) => (
                        <Star key={k} className="h-4 w-4 fill-gold text-gold" />
                      ))}
                    </div>
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <Reveal key={i} delay={i * 0.05}>
                <TestimonialCard testimonial={t} testId={`testimonial-${i}`} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <FinalCTA />
    </div>
  );
}

function TestimonialCard({ testimonial, testId }) {
  return (
    <div
      data-testid={testId}
      className="relative rounded-sm border border-navy-900/10 bg-white p-8 h-full flex flex-col transition-all duration-300 hover:-translate-y-1 hover:border-gold/50 hover:shadow-[0_30px_60px_-30px_rgba(10,25,47,0.25)]"
    >
      <Quote className="h-7 w-7 text-gold" />
      <p className="mt-5 font-display text-xl md:text-[1.35rem] leading-snug italic text-navy-900">
        “{testimonial.quote}”
      </p>
      <div className="mt-auto pt-8 flex items-center justify-between">
        <div>
          <div className="text-sm font-medium text-navy-900">{testimonial.name}</div>
          <div className="text-xs uppercase tracking-[0.18em] mt-1 text-navy-900/50">
            {testimonial.matter}
          </div>
        </div>
        <div className="flex gap-0.5">
          {[...Array(5)].map((_, k) => (
            <Star key={k} className="h-4 w-4 fill-gold text-gold" />
          ))}
        </div>
      </div>
      {testimonial.footer && (
        <div className="mt-5 pt-5 text-[10px] uppercase tracking-[0.22em] font-semibold text-gold border-t border-navy-900/10">
          {testimonial.footer}
        </div>
      )}
    </div>
  );
}
